import { formatMoney } from "@/lib/order-status";

/**
 * An order total, in euros, with the original currency underneath.
 *
 * Farfetch prices in the customer's currency: of 12 live orders only 3 were EUR,
 * the rest HKD, SAR, BRL, CAD, DKK, GBP and USD. Showing those raw put
 * `3,074.00 HKD` and `297.00 EUR` in the same column looking comparable when
 * they are ~10x apart, so a glance down the Total column meant nothing.
 *
 * Euro leads because that is the figure Burresi books and reconciles. The
 * original stays visible underneath because it is what the customer actually
 * paid and what appears on the Farfetch remittance - dropping it would make the
 * two impossible to match up.
 *
 * `totalEur` is null when no rate is available (the ECB publishes 29
 * currencies). Then the original is shown ALONE rather than a converted number
 * we cannot stand behind - the same rule the sale booking follows when it skips
 * an unconvertible order.
 */
export function OrderTotal({
	totalWithShip,
	currency,
	totalEur,
}: {
	totalWithShip: string | number | null;
	currency: string | null;
	totalEur: number | null;
}) {
	const original = formatMoney(totalWithShip, currency);
	const isEur = (currency ?? "").toUpperCase() === "EUR";

	// Already euros, or unconvertible: one line, no redundant repetition.
	if (isEur || totalEur === null || totalEur === undefined) {
		return <span>{original}</span>;
	}

	return (
		<span className="inline-flex flex-col items-end leading-tight">
			<span>{formatMoney(totalEur, "EUR")}</span>
			<span className="text-xs text-muted-foreground">{original}</span>
		</span>
	);
}
